import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { isNil } from "lodash";
import { memo, useCallback, useState } from "react";
import { useOutboundBenchEdiPayloadQuery } from "../../../generated/graphql";

const styles = {
  block: {
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
  } satisfies SxProps<Theme>,
  header: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  } satisfies SxProps<Theme>,
  json: {
    m: 0,
    p: 1,
    maxHeight: 400,
    overflow: "auto",
    fontFamily: "monospace",
    fontSize: 12,
    bgcolor: "action.hover",
    borderRadius: 1,
    whiteSpace: "pre",
  } satisfies SxProps<Theme>,
};

/** How long the copy button says "Copied". */
const COPIED_MS = 2000;

type JsonBlockProps = {
  readonly title: string;
  readonly text: string;
};

const JsonBlock = memo(({ title, text }: JsonBlockProps) => {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopyError(null);
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, COPIED_MS);
      },
      (error: unknown) => {
        setCopyError(
          error instanceof Error ? error.message : "Could not copy.",
        );
      },
    );
  }, [text]);
  return (
    <Box sx={styles.block}>
      <Box sx={styles.header}>
        <Typography variant="subtitle2">{title}</Typography>
        <Button size="small" variant="outlined" onClick={handleCopy}>
          {copied ? "Copied" : "Copy"}
        </Button>
        {copyError !== null && (
          <Typography color="error" variant="body2">
            {copyError}
          </Typography>
        )}
      </Box>
      <Box component="pre" sx={styles.json}>
        {text}
      </Box>
    </Box>
  );
});
JsonBlock.displayName = "JsonBlock";

/**
 * The payload behind one outbound EDI row, fetched when the operator expands
 * the row: the request the TMS posted to Tenedi. A row with no recorded
 * request shows its stored payload instead, under the note that says why:
 * the row may have sent nothing, or Tenedi may have received a request the
 * bench cannot rebuild.
 */
const EdiPayloadPanel = ({
  ediTransactionUuid,
}: {
  readonly ediTransactionUuid: string;
}) => {
  // The internal app's queries default to `errorPolicy: "ignore"`, which
  // would show an empty panel for a refused row instead of the reason.
  const { data, loading, error } = useOutboundBenchEdiPayloadQuery({
    variables: { ediTransactionUuid },
    errorPolicy: "none",
  });
  if (loading) {
    return <CircularProgress size={20} />;
  }
  if (error !== undefined) {
    return <Alert severity="error">{error.message}</Alert>;
  }
  const view = data?.outboundBenchEdiPayload;
  if (view === undefined) {
    return null;
  }
  return (
    <Stack spacing={1}>
      <Alert severity={view.deliveredThroughTenedi ? "info" : "warning"}>
        {view.note}
      </Alert>
      {isNil(view.sentBody) ? (
        <JsonBlock text={view.storedPayload} title="Stored payload" />
      ) : (
        <JsonBlock
          text={view.sentBody}
          title={`Sent to Tenedi: POST ${view.sentPath ?? ""}`}
        />
      )}
    </Stack>
  );
};

export default EdiPayloadPanel;
